import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';

class EzSpinnerTypeInfo {
    /**
     * @static
     * @public @readonly @property
     * Gets the constant default width for a spinner as: 48
     * @returns {string|number}
     */
    static get DEFAULT_WIDTH() {
        return 48;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default attributes for the EzSpinnerTypeInfo
     * @returns {object}
     */
    static get DEFAULT_ATTRIBUTES() {
        return {
            canResize: false,
            width: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            height: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            maxWidth: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            minWidth: EzNumber.ZERO
        };
    }

    /**
     * @private @field
     * Stores the id of the spinner
     * @type {string}
     */
    #id = null;
    /**
     * @public @property @getter
     * Gets the id of the spinner
     * @returns {string}
     */
    get id() {
        return this.#id;
    }
    /**
     * @public @property @setter
     * Sets the id of the spinner
     * @param {string} id
     */
    set id(id) {
        if (!EzString.hasLength(id)) {
            throw new EzBadParamException(
                'id',
                EzSpinnerTypeInfo,
                'EzSpinnerTypeInfo property setter id(id)');
        }

        this.#id = id;
    }

    /**
     * @private @field
     * Stores the spinner asset's url
     * @type {string}
     */
    #assetUrl = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the spinner asset's url
     * @returns {string}
     */
    get assetUrl() {
        return this.#assetUrl;
    }
    /**
     * @public @property @setter
     * Sets the spinner asset's url
     * @param {string} id
     */
    set assetUrl(assetUrl) {
        if (!EzString.hasLength(assetUrl)) {
            throw new EzBadParamException(
                'assetUrl',
                EzSpinnerTypeInfo,
                'EzSpinnerTypeInfo property setter assetUrl(id)');
        }

        this.#assetUrl = assetUrl;
    }

    /**
     * @private @field
     * Stores the spinner's display name
     * @type {string}
     */
    #displayName = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets the spinner's display name
     * @returns {string}
     */
    get displayName() {
        if (!EzString.hasLength(this.#displayName)) {
            this.#spinnerType = this.id;
        }

        return EzString.stringWithLengthOrDefault(
            this.#displayName,
            this.id);
    }
    /**
     * @public @property @setter
     * Sets the spinner's display name
     * @param {string} id
     */
    set displayName(displayName) {
        this.#displayName = EzString.stringWithLengthOrDefault(
            displayName,
            this.id);
    }

    /**
     * @private @field
     * Stores the id of the spinner
     * @type {string}
     */
    #spinnerType = EzString.EMPTY;
    /**
     * @public @property @getter
     * Gets
     * @returns {string}
     */
    get spinnerType() {
        if (!EzString.hasLength(this.#spinnerType)) {
            this.#spinnerType = 'gif';
        }

        return this.#spinnerType;
    }
    /**
         * @public @property @setter
         * Sets
         * @param {string} id
         */
    set spinnerType(spinnerType) {
        this.#spinnerType = EzString.stringWithLengthOrDefault(
            spinnerType,
            'gif');
    }

    /**
     * @private @field
     * Stores the additional ttributes for the EzSpinnerTypeInfo
     * @type {object}
     */
    #attributes = null;
    /**
     * @public @property @getter
     * Gets the additional ttributes for the EzSpinnerTypeInfo
     * @returns {object}
     */
    get attributes() {
        if (!EzObject.isValid(this.#attributes)) {
            this.attributes = EzSpinnerTypeInfo.DEFAULT_ATTRIBUTES;
        }

        return this.#attributes;
    }
    /**
     * @public @property @setter
     * Sets the additional ttributes for the EzSpinnerTypeInfo
     * @param {object} attributes
     * If undefined or null, then the default value of EzSpinnerTypeInfo.DEFAULT_ATTRIBUTES is used.
     */
    set attributes(attributes = null) {
        if (!EzObject.isValid(attributes)) {
            attributes = EzSpinnerTypeInfo.DEFAULT_ATTRIBUTES;
        }

        this.#attributes.canResize = EzBoolean.booleanOrFalse(attributes?.canResize);

        this.#attributes.width = EzObject.assignOrDefault(
            attributes?.width,
            EzSpinnerTypeInfo.DEFAULT_WIDTH);

        this.#attributes.height = EzObject.assignOrDefault(
            attributes?.height,
            this.#attributes.width);

        if (EzBoolean.isFalse(this.#attributes.canResize)) {
            this.#attributes.maxWidth = this.#attributes.width;

            this.#attributes.minWidth = this.attributes.width;
        } else {
            this.#attributes.maxWidth = EzObject.assignOrDefault(
                attributes?.maxWidth,
                null);

            this.#attributes.minWidth = EzObject.assignOrDefault(
                attributes?.minWidth,
                0);
        }
    }

    /**
     * @public @property @getter
     * Gets if the spinner can resize above or below it's initial width and height.
     * @returns {boolean}
     */
    get canResize() {
        if (!EzBoolean.isBoolean(this.attributes.canResize)) {
            this.attributes.canResize = false;
        }

        return this.attributes.canResize;
    }
    /**
     * @public @property @setter
     * Sets if the spinner can resize above or below it's initial width and height.
     * @param {undefined|null|boolean} canResize
     * Default: false
     */
    set canResize(canResize = false) {
        this.attributes.canResize = EzBoolean.booleanOrFalse(canResize);
    }

    /**
     * @public @property @getter
     * Gets the initial width of the spinner asset
     * @returns {string|number}}
     */
    get width() {
        if (!EzNumber.isNumber(this.attributes.width) && !EzString.hasLength(this.attributes.width)) {
            this.attributes.width = EzSpinnerTypeInfo.DEFAULT_WIDTH;
        }

        return this.attributes.width;
    }
    /**
     * @public @property @setter
     * Sets the initial width of the spinner asset
     * @param {undefined|null|string|number} width
     * Defualt: EzSpinnerTypeInfo.DEFAULT_WIDTH
     */
    set width(width = null) {
        if (EzNumber.isNumber(width) && 0 > width) {
            throw new EzBadParamException(
                'width',
                this,
                'EzSpinnerTypeInfo property setter for width.',
                'Param width must be greater than or equal to zero.');
        }

        this.attributes.width = EzNumber.isNumber(width) || EzString.hasLength(width)
            ? width
            : EzSpinnerTypeInfo.DEFAULT_WIDTH;
    }

    /**
     * @public @property @getter
     * Gets the initial height of the spinner asset
     * @returns {string|number}}
     */
    get height() {
        if (!EzNumber.isNumber(this.attributes.height) && !EzString.hasLength(this.attributes.height)) {
            this.attributes.height = this.width;
        }

        return this.attributes.height;
    }
    /**
     * @public @property @setter
     * Sets the initial height of the spinner asset
     * @param {undefined|null|string|number} width
     * Default: this.width
     */
    set height(height = null) {
        if (EzNumber.isNumber(height) && 0 > height) {
            throw new EzBadParamException(
                'height',
                this,
                'EzSpinnerTypeInfo property setter for height.',
                'Param height must be greater than or equal to zero.');
        }

        this.attributes.height = EzNumber.isNumber(height) || EzString.hasLength(height)
            ? height
            : this.width;
    }

    /**
     * @public @property @getter
     * Gets the maximum width of the spinner in pixels
     * A null value indicates 'unlimited' and can only be set if this.attributes.canResize is true
     *
     * @returns {null|string|number}
     */
    get maxWidth() {
        if (EzBoolean.isTrue(this.attributes.canResize)) {
            return null;
        }

        return EzNumber.isNumber(this.attributes.maxWidth) && 0 <= this.attributes.maxWidth
            ? this.maxWidth
            : this.width;
    }
    /**
     * @public @property @setter
     * Sets the maximum width of the spinner in pixels
     * A null value indicates 'unlimited' and can only be set if this.attributes.canResize is true
     *
     * @param {undefined|null|string|number} maxWidth
     * Default: null
     */
    set maxWidth(maxWidth = null) {
        if (EzString.isString(maxWidth)) {
            maxWidth = EzNumber.asIntOrDefault(maxWidth, this.width);
        }
        if (EzNumber.isNumber(maxWidth) && 0 > maxWidth) {
            throw new EzBadParamException(
                maxWidth,
                this,
                `set ${this.maxWidth}`);
        }

        this.maxWidth = EzBoolean.isTrue(this.attributes.canResize)
            ? null
            : EzNumber.numberOrDefault(
                maxWidth,
                this.width);
    }

    /**
     * @public @property @getter
     * Gets the minimum width of the spinner (assumed in pixels)
     * A null value indicates 'unlimited' and can only be set if this.attributes.canResize is true
     *
     * @returns {null|number}
     */
    get minWidth() {
        if (EzBoolean.isTrue(this.attributes.canResize)) {
            return null;
        }

        return EzNumber.isNumber(this.attributes.minWidth) && 0 <= this.attributes.minWidth
            ? this.minWidth
            : this.width;
    }
    /**
     * @public @property @setter
     * Sets the minimum width of the spinner in pixels
     * A null value indicates 'unlimited' and can only be set if this.attributes.canResize is true
     *
     * @param {undefined|null|string|number} minWidth
     * Default: null
     */
    set minWidth(minWidth = null) {
        if (EzString.isString(minWidth)) {
            minWidth = EzNumber.asIntOrDefault(minWidth, EzNumber.ZERO);
        }
        if (EzNumber.isNumber(minWidth) && 0 > minWidth) {
            throw new EzBadParamException(
                minWidth,
                this,
                `getter ${this.minWidth}`);
        }

        this.minWidth = EzBoolean.isTrue(this.attributes.canResize)
            ? null
            : EzNumber.numberOrDefault(
                this.width,
                minWidth);
    }

    /**
     * @public @constructor
     * Creates a new instance of EzSpinnerTypeInfo
     * @param {string} id
     * Param is required
     * @param {string} assetUrl
     * Param is required
     * @param {undefined|null|string} displayName
     * If undefined or null, then displayName is set equal to id
     * @param {undefined|null|string} spinnerType
     * Expecting one of the following: 'gif' or 'svg'
     * Default: 'gif'
     * @param {undefined|null|boolean} canResize
     * Defualt: false
     * @param {undefined|null|string|number} width
     * Default: 48
     * @param {undefined|null|string|number} height
     * If undefiend or null, then height is set equal to width.
     * @param {undefined|null|string|number} maxWidth
     * Default:
     *  When canSize = true, then null
     *  When canSize = false, then equal to this.width
     * @param {undefined|null|string|number} minWidth
     * Default:
     *  When canSize = true, then 0
     *  When canSize = false, then equal to this.width
     */
    constructor(id, assetUrl, displayName = EzString.EMPTY, spinnerType = 'gif', attributes = null) {
        if (!EzString.hasLength(id)) {
            throw new EzBadParamException(
                'id',
                this,
                this.constructor);
        }
        if (!EzString.hasLength(assetUrl)) {
            throw new EzBadParamException(
                'assetUrl',
                this,
                this.constructor);
        }

        this.id = id;

        this.assetUrl = assetUrl;

        this.displayName = EzObject.assignOrNull(displayName);

        this.spinnerType = EzObject.assignOrNull(spinnerType);

        this.#attributes = {
            canResize: false,
            width: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            height: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            maxWidth: EzSpinnerTypeInfo.DEFAULT_WIDTH,
            minWidth: EzNumber.ZERO
        };

        this.attributes = EzObject.assignOrDefault(
            attributes,
            EzSpinnerTypeInfo.DEFAULT_ATTRIBUTES);
    }
}

/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * The spinners available to display
 * ---------------------------------------------------------------------
 *     import { EzSpinnerType } from '/public/webcomponents/spinner/EzSpinnerType.js';
 * ---------------------------------------------------------------------
 */
export class EzSpinnerType extends EzEnumeration2 {
    /**
     * @static
     * @public @medthod
     * Returns the spinner configuration for the provided enumPropertyName
     * @param {string} ezSpinnerType
     * A valid enum property name or value from EzSpinnerType
     * @returns {object}
     */
    static ezSpinnerConfig(ezSpinnerType) {
        return this.ezEnumData(ezSpinnerType);
    }

    /**
     * @static
     * @public @medthod
     * Returns the full url of the spinner's image asset for the provided enumPropertyNameOrValue
     * @param {string} ezSpinnerType
     * A valid enum property name or value from EzSpinnerType
     * @returns {string}
     */
    static ezAssetUrl(ezSpinnerType) {
        return globalThis.ezApi.ezclocker.ezNavigation.ezGetPublicPageUrl(
            this.ezEnumData(ezSpinnerType).assetUrl);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzSpinnerType}
     */
    static #ezSpinnerTypeSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezBuildEnumeration() {
        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'UNKNOWN',
            'UNKNOWN',
            new EzSpinnerTypeInfo(
                // id
                'UNKNOWN',
                // assetUrl
                '/public/webcomponents/spinner/assets/gear-172px.svg',
                // displayName
                'Gear SVG',
                // spinnerType
                'gif',
                {
                    canResize: true,
                    // Pixels
                    width: 172,
                    height: 172
                }));

        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'GEAR_SVG',
            'GEAR_SVG',
            new EzSpinnerTypeInfo(
                // id
                'GEAR_SVG',
                // assetUrl
                '/public/webcomponents/spinner/assets/gear-172px.svg',
                // displayName
                'Gear SVG',
                // spinnerType
                'svg',
                {
                    canResize: true,
                    // Pixels
                    width: 172,
                    height: 172
                }));

        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'INFINITY_GIF',
            'INFINITY_GIF',
            new EzSpinnerTypeInfo(
                // id
                'INFINITY_GIF',
                // assetUrl
                '/public/webcomponents/spinner/assets/infinity.gif',
                // displayName
                'Infinity GIF',
                // spinnerType
                'gif',
                {
                    canResize: false,
                    // Pixels
                    width: 200,
                    height: 92,
                    maxWidth: 200,
                    minWidth: 92
                }));

        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'INFINITY_SNAKE_SVG',
            'INFINITY_SNAKE_SVG',
            new EzSpinnerTypeInfo(
                // id
                'INFINITY_SNAKE_SVG',
                // assetUrl
                '/public/webcomponents/spinner/assets/infinity-snake-spinner-orange.svg',
                // displayName
                'Infinity Snake SVG',
                // spinnerType
                'svg',
                {
                    canResize: true,
                    // Pixels
                    width: 200,
                    height: 92
                }));

        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'LINE_SPINNER_SVG',
            'LINE_SPINNER_SVG',
            new EzSpinnerTypeInfo(
                // id
                'LINE_SPINNER_SVG',
                // assetUrl
                '/public/webcomponents/spinner/assets/line-spinner-orange.svg',
                // displayName
                'Line Spinner SVG',
                // spinnerType
                'svg',
                {
                    canResize: true,
                    // Pixels
                    width: 100,
                    height: 100
                }));

        EzSpinnerType.#ezSpinnerTypeSingleton.ezBuildEnumProp(
            'EZCLOCKER_GIF',
            'EZCLOCKER_GIF',
            new EzSpinnerTypeInfo(
                // id
                'EZCLOCKER_GIF',
                // assetUrl
                '/public/webcomponents/spinner/assets/ezclocker-172px.gif',
                // displayName
                'EzClocker GIF',
                // spinnerType
                'gif',
                {
                    canResize: false,
                    // Pixels
                    width: 100,
                    height: 100,
                    maxWidth: 100,
                    minWidth: 100
                }));
    }

    /**
     * Static Initialization of EzSpinnerType
     */
    static {
        if (null == EzSpinnerType.#ezSpinnerTypeSingleton) {
            EzSpinnerType.#ezSpinnerTypeSingleton = new EzSpinnerType();

            EzSpinnerType.#ezBuildEnumeration();
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
